import React, { useEffect, useRef, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import uploadService from '../../services/upload.service';
import workplaceService from '../../services/workplace.service';

const MOBILE_APP_URL = 'https://effzone.sk/mobile-app';

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const downloadSizes = [
  { size: 256, primary: 'Small', secondary: '256 x 256 px' },
  { size: 512, primary: 'Medium', secondary: '512 x 512 px' },
  { size: 1024, primary: 'Large', secondary: '1024 x 1024 px (recommended for print)' },
  { size: 2048, primary: 'Extra Large', secondary: '2048 x 2048 px (for high-quality posters)' },
];

const dotTypes = ['square', 'dots', 'rounded', 'extra-rounded', 'classy', 'classy-rounded'];

const qrDefaults = {
  dotColor: '#111827',
  dotType: 'rounded',
  qrSize: 320,
  logoSize: 0.2,
  logoMargin: 0,
  logoUrl: '',
};

const getStyleFromWorkplace = (workplace) => {
  if (!workplace?.qr_style_json) {
    return {
      dotColor: qrDefaults.dotColor,
      dotType: qrDefaults.dotType,
      logoSize: qrDefaults.logoSize,
      logoMargin: qrDefaults.logoMargin,
    };
  }

  try {
    const parsed = JSON.parse(workplace.qr_style_json);
    return {
      dotColor: parsed?.dotColor || qrDefaults.dotColor,
      dotType: parsed?.dotType || qrDefaults.dotType,
      logoSize: typeof parsed?.logoSize === 'number' ? parsed.logoSize : qrDefaults.logoSize,
      logoMargin: typeof parsed?.logoMargin === 'number' ? parsed.logoMargin : qrDefaults.logoMargin,
    };
  } catch (error) {
    return {
      dotColor: qrDefaults.dotColor,
      dotType: qrDefaults.dotType,
      logoSize: qrDefaults.logoSize,
      logoMargin: qrDefaults.logoMargin,
    };
  }
};

const WorkplaceQREditor = ({ qrCodeUrl, workplace, onWorkplaceUpdated }) => {
  const qrRef = useRef(null);
  const qrCodeRef = useRef(null);
  const fileInputRef = useRef(null);
  const styleSaveTimeoutRef = useRef(null);
  const isHydratingStyleRef = useRef(true);

  const initialStyle = getStyleFromWorkplace(workplace);

  const [dotColor, setDotColor] = useState(initialStyle.dotColor);
  const [dotType, setDotType] = useState(initialStyle.dotType);
  const [qrSize] = useState(qrDefaults.qrSize);
  const [logoSize, setLogoSize] = useState(initialStyle.logoSize);
  const [logoMargin, setLogoMargin] = useState(initialStyle.logoMargin);
  const [logoUrl, setLogoUrl] = useState(workplace?.qr_logo_url || qrDefaults.logoUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    setLogoUrl(workplace?.qr_logo_url || '');
  }, [workplace?.id, workplace?.qr_logo_url]);

  useEffect(() => {
    const style = getStyleFromWorkplace(workplace);
    isHydratingStyleRef.current = true;
    setDotColor(style.dotColor);
    setDotType(style.dotType);
    setLogoSize(style.logoSize);
    setLogoMargin(style.logoMargin);
  }, [workplace?.id, workplace?.qr_style_json]);

  useEffect(() => {
    if (!workplace?.id) return;
    if (isHydratingStyleRef.current) {
      isHydratingStyleRef.current = false;
      return;
    }

    if (styleSaveTimeoutRef.current) {
      clearTimeout(styleSaveTimeoutRef.current);
    }

    styleSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await workplaceService.updateWorkplace(workplace.id, {
          ...workplace,
          qr_logo_url: logoUrl || null,
          qr_style_json: JSON.stringify({
            dotColor,
            dotType,
            logoSize,
            logoMargin,
          }),
        });
      } catch (error) {
        setUploadError(error?.response?.data?.message || 'QR setup save failed.');
      }
    }, 450);

    return () => {
      if (styleSaveTimeoutRef.current) {
        clearTimeout(styleSaveTimeoutRef.current);
      }
    };
  }, [dotColor, dotType, logoMargin, logoSize, logoUrl, workplace?.id, workplace?.name, workplace?.latitude, workplace?.longitude, workplace?.geofencing_enabled, workplace?.geofencing_radius, workplace?.qr_code_id, workplace?.qr_style_json]);

  useEffect(() => {
    if (!qrRef.current || qrCodeRef.current) return;

    qrCodeRef.current = new QRCodeStyling({
      width: qrSize,
      height: qrSize,
      type: 'svg',
      data: qrCodeUrl,
      dotsOptions: {
        color: dotColor,
        type: dotType,
      },
      backgroundOptions: {
        color: '#FFFFFF',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: logoMargin,
        imageSize: logoSize,
      },
    });

    qrCodeRef.current.append(qrRef.current);
  }, [dotColor, dotType, logoMargin, logoSize, qrCodeUrl, qrSize]);

  useEffect(() => {
    if (!qrCodeRef.current) return;

    qrCodeRef.current.update({
      data: qrCodeUrl,
      width: qrSize,
      height: qrSize,
      dotsOptions: {
        color: dotColor,
        type: dotType,
      },
      image: logoUrl || undefined,
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: logoMargin,
        imageSize: logoSize,
      },
    });
  }, [dotColor, dotType, logoMargin, logoSize, logoUrl, qrCodeUrl, qrSize]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const persistLogoUrl = async (nextLogoUrl) => {
    if (!workplace?.id) return;

    await workplaceService.updateWorkplace(workplace.id, {
      ...workplace,
      qr_logo_url: nextLogoUrl || null,
    });

    if (onWorkplaceUpdated) {
      onWorkplaceUpdated();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setIsUploading(true);
    try {
      const uploadedPath = await uploadService.uploadWorkplaceQrLogo(file);
      const absoluteUrl = uploadedPath.startsWith('http')
        ? uploadedPath
        : `${window.location.origin}${uploadedPath}`;
      await persistLogoUrl(absoluteUrl);
      setLogoUrl(absoluteUrl);
    } catch (error) {
      setUploadError(error?.response?.data?.message || 'Logo upload failed.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setUploadError('');
    setIsUploading(true);
    try {
      await persistLogoUrl('');
      setLogoUrl('');
    } catch (error) {
      setUploadError(error?.response?.data?.message || 'Logo removal failed.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (size) => {
    if (!qrCodeRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      qrCodeRef.current.update({
        data: qrCodeUrl,
        width: size,
        height: size,
        dotsOptions: {
          color: dotColor,
          type: dotType,
        },
        image: logoUrl || undefined,
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: logoMargin,
          imageSize: logoSize,
        },
      });
      // Give QR renderer a tick before extracting raw data.
      await new Promise((resolve) => setTimeout(resolve, 120));

      const blob = await qrCodeRef.current.getRawData('png');
      if (!blob) {
        throw new Error('Failed to generate QR image.');
      }

      const fileName = `${workplace?.name || 'workplace'}-qrcode-${size}.png`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setUploadError(error?.message || 'QR download failed.');
    } finally {
      qrCodeRef.current.update({
        data: qrCodeUrl,
        width: qrSize,
        height: qrSize,
        dotsOptions: {
          color: dotColor,
          type: dotType,
        },
        image: logoUrl || undefined,
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: logoMargin,
          imageSize: logoSize,
        },
      });
      setIsDownloading(false);
    }
  };

  const handlePrintPoster = async () => {
    if (!qrCodeRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      // 1) Hlavný QR pracoviska vo vysokej kvalite (aktuálny štýl)
      qrCodeRef.current.update({
        data: qrCodeUrl,
        width: 1024,
        height: 1024,
        dotsOptions: { color: dotColor, type: dotType },
        image: logoUrl || undefined,
        imageOptions: { crossOrigin: 'anonymous', margin: logoMargin, imageSize: logoSize },
      });
      await new Promise((resolve) => setTimeout(resolve, 150));
      const workplaceQrBlob = await qrCodeRef.current.getRawData('png');
      if (!workplaceQrBlob) throw new Error('Failed to generate QR image.');
      const workplaceQrDataUrl = await blobToDataUrl(workplaceQrBlob);

      // 2) Malý QR na stiahnutie mobilnej appky
      const appQr = new QRCodeStyling({
        width: 512,
        height: 512,
        type: 'svg',
        data: MOBILE_APP_URL,
        dotsOptions: { color: '#111827', type: 'rounded' },
        backgroundOptions: { color: '#FFFFFF' },
      });
      const appQrBlob = await appQr.getRawData('png');
      const appQrDataUrl = appQrBlob ? await blobToDataUrl(appQrBlob) : '';

      const workplaceName = workplace?.name || '';
      const posterHtml = `<!DOCTYPE html>
<html lang="sk">
<head>
<meta charset="UTF-8" />
<title>Effzone – ${workplaceName}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 210mm; height: 297mm; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; display: flex; flex-direction: column; align-items: center; padding: 18mm 16mm; }
  .brand { font-size: 26px; font-weight: 800; letter-spacing: 0.5px; }
  .workplace { font-size: 20px; font-weight: 600; color: #374151; margin-top: 4mm; }
  .headline { font-size: 30px; font-weight: 800; text-align: center; margin-top: 10mm; }
  .qr { width: 120mm; height: 120mm; margin-top: 8mm; }
  .steps { margin-top: 10mm; font-size: 15px; line-height: 1.9; color: #374151; align-self: center; }
  .steps b { color: #111827; }
  .footer { margin-top: auto; display: flex; align-items: center; gap: 8mm; border-top: 1px solid #e5e7eb; padding-top: 6mm; width: 100%; justify-content: center; }
  .footer .app-qr { width: 28mm; height: 28mm; }
  .footer .hint { font-size: 13px; color: #374151; line-height: 1.6; max-width: 90mm; }
</style>
</head>
<body>
  <div class="brand">Effzone</div>
  ${workplaceName ? `<div class="workplace">${workplaceName}</div>` : ''}
  <div class="headline">Naskenuj a zapíš si dochádzku</div>
  <img class="qr" src="${workplaceQrDataUrl}" alt="QR kód pracoviska" />
  <div class="steps">
    <div><b>1.</b> Stiahni si aplikáciu <b>Effzone</b> (QR kód dole)</div>
    <div><b>2.</b> Naskenuj tento QR kód aplikáciou</div>
    <div><b>3.</b> Prihlás sa svojím telefónnym číslom (príde ti SMS kód)</div>
  </div>
  <div class="footer">
    ${appQrDataUrl ? `<img class="app-qr" src="${appQrDataUrl}" alt="QR na stiahnutie aplikácie" />` : ''}
    <div class="hint">Aplikáciu Effzone stiahneš naskenovaním tohto QR kódu alebo na <b>effzone.sk/mobile-app</b> (App Store aj Google Play).</div>
  </div>
  <script>window.onload = function () { window.focus(); window.print(); };</script>
</body>
</html>`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) throw new Error('Prehliadač zablokoval nové okno. Povoľte pop-up okná.');
      printWindow.document.open();
      printWindow.document.write(posterHtml);
      printWindow.document.close();
    } catch (error) {
      setUploadError(error?.message || 'Poster generation failed.');
    } finally {
      qrCodeRef.current.update({
        data: qrCodeUrl,
        width: qrSize,
        height: qrSize,
        dotsOptions: { color: dotColor, type: dotType },
        image: logoUrl || undefined,
        imageOptions: { crossOrigin: 'anonymous', margin: logoMargin, imageSize: logoSize },
      });
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
        <Box ref={qrRef} sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
        <Typography variant="body2" sx={{ mt: 2, wordBreak: 'break-all', color: 'text.secondary', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
          {qrCodeUrl}
        </Typography>
      </Paper>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {uploadError && <Alert severity="error">{uploadError}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="outlined" startIcon={isUploading ? <CircularProgress size={16} /> : <FileUploadIcon />} onClick={handleUploadClick} disabled={isUploading}>
            Nahrať logo do stredu
          </Button>
          <Button variant="text" color="inherit" onClick={handleRemoveLogo} disabled={!logoUrl || isUploading}>
            Odobrať logo
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            type="color"
            label="Farba bodov"
            value={dotColor}
            onChange={(event) => setDotColor(event.target.value)}
            sx={{ width: { xs: '100%', sm: 140 } }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Typ bodov</InputLabel>
            <Select value={dotType} label="Typ bodov" onChange={(event) => setDotType(event.target.value)}>
              {dotTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Veľkosť loga: {Math.round(logoSize * 100)}%
          </Typography>
          <Slider value={logoSize} onChange={(event, value) => setLogoSize(value)} min={0.1} max={0.4} step={0.01} valueLabelDisplay="auto" />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Odsadenie loga: {logoMargin}px
          </Typography>
          <Slider value={logoMargin} onChange={(event, value) => setLogoMargin(value)} min={-20} max={40} step={1} valueLabelDisplay="auto" />
        </Box>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        size="large"
        startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
        onClick={handlePrintPoster}
        disabled={isDownloading}
        sx={{ mt: 4, borderRadius: '12px', boxShadow: 'none', py: 1.25, fontWeight: 700 }}
      >
        Vytlačiť A4 plagát (QR + návod pre zamestnancov)
      </Button>

      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
        Download QR Code
      </Typography>
      <List component={Paper} elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 1, border: '1px solid', borderColor: 'divider' }}>
        {downloadSizes.map((item) => (
          <ListItem
            key={item.size}
            disablePadding
            secondaryAction={(
              <Button
                startIcon={isDownloading ? <CircularProgress size={14} /> : <FileDownloadIcon />}
                onClick={() => handleDownload(item.size)}
                disabled={isDownloading}
              >
                PNG
              </Button>
            )}
          >
            <ListItemButton>
              <ListItemText primary={item.primary} secondary={item.secondary} primaryTypographyProps={{ fontWeight: '500' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default WorkplaceQREditor;
